import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, Subscription, auditTime, catchError, combineLatest, firstValueFrom, from, interval, map, of, switchMap, take, tap } from 'rxjs';
import { GlobalVariablesService } from '@app/core/services/global-variables.service';
import { MediaPlaybackService } from '@app/shared/services/media-playback.service';
import { ToastNotifyService } from './toast-notify.service';
import { NotificationHubConnectionService } from '@app/core/services/hubs/notification-hub-connection.service';
import 'firebase/compat/messaging';
import * as firebase from 'firebase/compat/app';

export class NotificationsData {
  constructor(
    public totalPages: number,
    public pageSize: number,
    public currentPage: number,
    public totalMessages: number,
    public unreadMessages: number
  ) { }
}

export interface NotificationListItem {
  id: string;
  eventId: string;
  topic: string;
  severity: string;
  titleKey?: string;
  bodyKey?: string;
  title?: string;
  body?: string;
  payloadJson?: string;
  payload?: Record<string, unknown>;
  source?: string;
  link?: string;
  createdAtUtc: string;
  readAtUtc?: string | null;
  archivedAtUtc?: string | null;
  dismissedAtUtc?: string | null;
  timestamp: string;
  read: boolean;
  notification: { title: string; body: string };
  fcmOptions?: { link?: string };
}

export interface NotificationListResponse {
  items: NotificationListItem[];
  total: number;
  unread: number;
}

export interface NotificationPreferenceDto {
  topic: string;
  channel: NotificationChannel;
  enabled: boolean;
  severityThreshold?: string;
  quietStartMinutes?: number | null;
  quietEndMinutes?: number | null;
}

export interface NotificationPreferencesResponse {
  topics: string[];
  channels: NotificationChannel[];
  preferences: NotificationPreferenceDto[];
}

export interface NotificationDeviceDto {
  id: string;
  tokenHash: string;
  platform?: string;
  browser?: string;
  deviceName?: string;
  enabled: boolean;
  registeredAtUtc: string;
  lastSeenAtUtc?: string | null;
}

export interface UserTelegramBindingDto {
  enabled: boolean;
  chatId?: number | null;
  chatTitle?: string | null;
  activationCode?: string | null;
  activatedAtUtc?: string | null;
  botName?: string | null;
  botUsername?: string | null;
  botUrl?: string | null;
  canSyncPersonalTopics: boolean;
  personalTopicError?: string | null;
  personalTopicRoutes: TelegramTopicRouteDto[];
}

export interface TelegramActivationResponse {
  activationCode: string;
  activationCommand: string;
  botName?: string | null;
  botUsername?: string | null;
  botUrl?: string | null;
  botActivationUrl?: string | null;
}

export interface TelegramTopicRouteDto {
  topic: string;
  chatId: number;
  messageThreadId: number;
  name?: string | null;
}

export interface TelegramGroupStatusDto {
  isAdmin: boolean;
  configured: boolean;
  botConfigured: boolean;
  chatConfigured: boolean;
  canSyncTopics: boolean;
  canInviteUsers: boolean;
  personalTelegramConnected: boolean;
  canCreateInvite: boolean;
  chatId?: number | null;
  chatTitle?: string | null;
  chatType?: string | null;
  forumEnabled: boolean;
  error?: string | null;
  inviteError?: string | null;
  botName?: string | null;
  botUsername?: string | null;
  botUrl?: string | null;
  groupActivationCommand?: string | null;
  routes: TelegramTopicRouteDto[];
}

export interface TelegramTopicSyncResponse {
  success: boolean;
  error?: string | null;
  routes: TelegramTopicRouteDto[];
}

export interface TelegramGroupInviteResponse {
  inviteLink: string;
  expiresAtUtc: string;
  chatId: number;
}

export interface NotificationMuteStateDto {
  isMuted: boolean;
  mutedUntilUtc?: string | null;
}

export type NotificationChannel = 'InApp' | 'BrowserPush' | 'Email' | 'Telegram';

export interface NotificationQuery {
  topic?: string;
  severity?: string;
  readState?: string;
  search?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly TOKEN_KEY = 'fcm_token';
  private readonly PAGE_SIZE = 10;
  private readonly FALLBACK_UNREAD_POLL_MS = 30_000;
  private readonly apiUrl: string;
  private currentPage = 1;
  private currentQuery: NotificationQuery = {};

  private token$ = new BehaviorSubject<string | null>(this.getTokenFromLocalStorage());
  private subscribedTopics$ = new BehaviorSubject<Set<string>>(new Set<string>());
  private messages$ = new BehaviorSubject<NotificationListItem[]>([]);
  private notificationsData$ = new BehaviorSubject<NotificationsData>(new NotificationsData(0, this.PAGE_SIZE, 1, 0, 0));
  private preferences$ = new BehaviorSubject<NotificationPreferencesResponse>({ topics: [], channels: [], preferences: [] });
  private confirmedPreferenceState: NotificationPreferencesResponse = { topics: [], channels: [], preferences: [] };
  private pendingPreferenceSave: { preferences: NotificationPreferenceDto[]; version: number } | null = null;
  private preferenceSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private preferenceSaveInFlight = false;
  private preferenceStateVersion = 0;
  private devices$ = new BehaviorSubject<NotificationDeviceDto[]>([]);
  private telegramBinding$ = new BehaviorSubject<UserTelegramBindingDto>(this.defaultTelegramBinding());
  private telegramGroupStatus$ = new BehaviorSubject<TelegramGroupStatusDto>(this.defaultTelegramGroupStatus());
  private muteState$ = new BehaviorSubject<NotificationMuteStateDto>({ isMuted: false, mutedUntilUtc: null });
  private muteRemainingSeconds$ = new BehaviorSubject<number>(0);
  private readonly messagingEnabled = this.isMessagingAvailable();
  private authenticatedSessionInitialized = false;
  private authenticatedSessionGeneration = 0;
  private authenticatedSessionInitialization?: Promise<void>;
  private readonly notificationRefreshRequests$ = new Subject<void>();
  private notificationRefreshSubscription?: Subscription;
  private unreadFallbackPollSubscription?: Subscription;
  private realtimeNotificationSubscription?: Subscription;
  private foregroundMessageSubscription?: { unsubscribe(): void };
  private tokenRefreshSubscription?: { unsubscribe(): void };
  private muteCountdownSubscription?: Subscription;

  constructor(
    private globals: GlobalVariablesService,
    private http: HttpClient,
    private mediaPlayback: MediaPlaybackService,
    private toastNotify: ToastNotifyService,
    private afMessaging: AngularFireMessaging,
    private notificationHub: NotificationHubConnectionService
  ) {
    this.apiUrl = `${this.globals.apiV2Address}/notifications`;
  }

  public initializeAuthenticatedSession(): Promise<void> {
    if (this.authenticatedSessionInitialized) {
      return this.authenticatedSessionInitialization ?? Promise.resolve();
    }

    this.authenticatedSessionInitialized = true;
    const sessionGeneration = ++this.authenticatedSessionGeneration;
    this.startMuteCountdown();

    let initialization: Promise<void>;
    initialization = firstValueFrom(this.refreshMuteState())
      .then(() => {
        if (!this.authenticatedSessionInitialized || sessionGeneration !== this.authenticatedSessionGeneration) {
          return;
        }

        this.refreshNotifications().subscribe();
        this.notificationRefreshSubscription = this.notificationRefreshRequests$
          .pipe(
            auditTime(1000),
            switchMap(() => this.refreshNotifications())
          )
          .subscribe();
        this.unreadFallbackPollSubscription = interval(this.FALLBACK_UNREAD_POLL_MS)
          .pipe(
            switchMap(() => {
              const previousUnread = this.notificationsData$.value.unreadMessages;
              return this.refreshUnreadCount().pipe(
                tap(unread => {
                  if (unread !== previousUnread) {
                    this.notificationRefreshRequests$.next();
                  }
                }),
                catchError(() => of(previousUnread))
              );
            })
          )
          .subscribe();
        this.notificationHub.init();
        this.realtimeNotificationSubscription = this.notificationHub.newNotification$
          .pipe(
            tap(message => this.applyRealtimeUnread(message?.unread)),
            auditTime(1000)
          )
          .subscribe(message => {
            this.notificationRefreshRequests$.next();
            if (!this.isWebNotificationMuted(message)) {
              this.playNotificationSoundIfNeeded();
            }
          });

        if (this.messagingEnabled) {
          this.listenToMessages();
        }
      })
      .finally(() => {
        if (this.authenticatedSessionInitialization === initialization) {
          this.authenticatedSessionInitialization = undefined;
        }
      });

    this.authenticatedSessionInitialization = initialization;
    return initialization;
  }

  public endAuthenticatedSession(): void {
    this.authenticatedSessionInitialized = false;
    this.authenticatedSessionGeneration += 1;
    this.authenticatedSessionInitialization = undefined;
    this.notificationRefreshSubscription?.unsubscribe();
    this.notificationRefreshSubscription = undefined;
    this.unreadFallbackPollSubscription?.unsubscribe();
    this.unreadFallbackPollSubscription = undefined;
    this.realtimeNotificationSubscription?.unsubscribe();
    this.realtimeNotificationSubscription = undefined;
    this.foregroundMessageSubscription?.unsubscribe();
    this.foregroundMessageSubscription = undefined;
    this.tokenRefreshSubscription?.unsubscribe();
    this.tokenRefreshSubscription = undefined;
    this.muteCountdownSubscription?.unsubscribe();
    this.muteCountdownSubscription = undefined;
    this.notificationHub.disconnect();
    this.resetSessionState();
  }

  public loadNotificationSettings(): void {
    this.refreshPreferences().subscribe();
    this.refreshDevices().subscribe();
    this.refreshTelegramBinding().subscribe();
    this.refreshTelegramGroup().subscribe();
  }

  public requestPermission(): Observable<boolean> {
    if (!this.messagingEnabled || typeof Notification === 'undefined') {
      return of(false);
    }

    return from(Notification.requestPermission()).pipe(
      switchMap((permission) => {
        if (permission !== 'granted') {
          return of(false);
        }

        return from(this.resolveFirebaseMessagingToken()).pipe(
          switchMap((token) => {
            if (!token) {
              return of(false);
            }

            return this.registerDevice(token, this.getTokenFromLocalStorage()).pipe(
              switchMap((device) => {
                if (!device) {
                  return of(false);
                }

                this.persistToken(token);
                return this.enableChannelForAllTopics('BrowserPush').pipe(map(() => true));
              })
            );
          })
        );
      }),
      catchError((error) => {
        console.error('FCM permission/token error:', error);
        return of(false);
      })
    );
  }

  public handleTokenRefresh(): void {
    if (!this.messagingEnabled || this.tokenRefreshSubscription) {
      return;
    }

    try {
      this.tokenRefreshSubscription = this.afMessaging.tokenChanges.subscribe({
        next: (newToken) => {
          const oldToken = this.getTokenFromLocalStorage();
          if (!newToken || newToken === oldToken) {
            return;
          }

          this.updateToken(newToken, oldToken);
        },
        error: (err) => console.error('Token refresh error:', err)
      });
    } catch (err) {
      console.error('Token refresh initialization error:', err);
    }
  }

  public subscribeToTopic(topic: string): Observable<any> {
    return this.setChannelPreference(topic, 'BrowserPush', true).pipe(
      tap(() => this.toastNotify.showSuccess('Notification', `Subscribed to ${topic}`))
    );
  }

  public unsubscribeToTopic(topic: string): Observable<any> {
    return this.setChannelPreference(topic, 'BrowserPush', false);
  }

  public cancelSubscription(): Observable<boolean> {
    return this.http.delete<void>(`${this.apiUrl}/devices`).pipe(
      switchMap(() => this.disableChannelForAllTopics('BrowserPush')),
      tap(() => this.updateToken(null)),
      map(() => true),
      catchError((error) => {
        console.error('Error disabling browser push notifications:', error);
        return of(false);
      })
    );
  }

  public isSubscribedToTopic(topic: string): Observable<boolean> {
    return this.subscribedTopics$.asObservable().pipe(map(topics => topics.has(topic)));
  }

  public isSubscribed(): Observable<boolean> {
    return combineLatest([this.token$, this.devices$]).pipe(
      map(([token, devices]) => !!token || devices.some(device => device.enabled))
    );
  }

  public getPaginatedMessages(): Observable<NotificationListItem[]> {
    return this.messages$.asObservable();
  }

  public setPage(newPage: number): void {
    this.currentPage = Math.max(1, newPage);
    this.refreshNotifications().subscribe();
  }

  public setQuery(query: NotificationQuery): void {
    this.currentQuery = this.normalizeNotificationQuery(query);
    this.currentPage = 1;
    this.refreshNotifications().subscribe();
  }

  public getNotificationsData(): Observable<NotificationsData> {
    return this.notificationsData$.asObservable();
  }

  public getPreferences(): Observable<NotificationPreferencesResponse> {
    return this.preferences$.asObservable();
  }

  public getDevices(): Observable<NotificationDeviceDto[]> {
    return this.devices$.asObservable();
  }

  public getTelegramBinding(): Observable<UserTelegramBindingDto> {
    return this.telegramBinding$.asObservable();
  }

  public getTelegramGroup(): Observable<TelegramGroupStatusDto> {
    return this.telegramGroupStatus$.asObservable();
  }

  public getMuteState(): Observable<NotificationMuteStateDto> {
    return this.muteState$.asObservable();
  }

  public getMuteRemainingSeconds(): Observable<number> {
    return this.muteRemainingSeconds$.asObservable();
  }

  public refreshNotifications(query: NotificationQuery = this.currentQuery): Observable<NotificationListResponse> {
    this.currentQuery = this.normalizeNotificationQuery(query);
    const params = this.buildNotificationParams(this.currentQuery, true);

    return this.http.get<NotificationListResponse>(this.apiUrl, { params }).pipe(
      map((response) => ({
        items: (response.items ?? []).map(item => this.mapNotificationItem(item)),
        total: response.total ?? 0,
        unread: response.unread ?? 0
      })),
      tap((response) => {
        this.messages$.next(response.items);
        this.updateNotificationsData(response.total, response.unread);
      }),
      catchError((error) => {
        console.error('Notification history refresh failed:', error);
        this.messages$.next([]);
        this.updateNotificationsData(0, 0);
        return of({ items: [], total: 0, unread: 0 });
      })
    );
  }

  public refreshUnreadCount(): Observable<number> {
    return this.http.get<{ unread: number }>(`${this.apiUrl}/unread-count`).pipe(
      map(response => response.unread ?? 0),
      tap(unread => {
        const current = this.notificationsData$.value;
        this.notificationsData$.next(new NotificationsData(current.totalPages, current.pageSize, current.currentPage, current.totalMessages, unread));
      })
    );
  }

  public addNewMessage(_message: any): void {
    this.refreshNotifications().subscribe();
  }

  public deleteMessage(index: number): void {
    const message = this.messages$.value[index];
    if (!message) {
      return;
    }

    this.deleteNotification(message.id).subscribe();
  }

  public deleteNotification(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      tap(() => this.refreshNotifications().subscribe())
    );
  }

  public archiveNotification(id: string): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}/archive`, {}).pipe(
      tap(() => this.refreshNotifications().subscribe())
    );
  }

  public clearMessages(): void {
    this.clearNotificationMessages({});
  }

  public deleteFilteredMessages(query: NotificationQuery): void {
    const deleteQuery = this.normalizeNotificationQuery(query);
    const params = this.buildNotificationParams(deleteQuery, false);

    this.http.delete<void>(this.apiUrl, { params }).pipe(
      tap(() => {
        this.currentPage = 1;
        this.refreshNotifications(deleteQuery).subscribe();
      })
    ).subscribe();
  }

  public markMessageAsRead(index: number): void {
    const message = this.messages$.value[index];
    if (!message || message.read) {
      return;
    }

    this.markAsRead(message.id).subscribe();
  }

  public markAsRead(id: string): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}/read`, {}).pipe(
      tap(() => this.refreshNotifications().subscribe())
    );
  }

  public markAsUnread(id: string): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}/unread`, {}).pipe(
      tap(() => this.refreshNotifications().subscribe())
    );
  }

  public refreshPreferences(): Observable<NotificationPreferencesResponse> {
    return this.http.get<NotificationPreferencesResponse>(`${this.apiUrl}/preferences`).pipe(
      tap((response) => {
        this.applyPreferenceState(response, true);
      }),
      catchError((error) => {
        console.error('Notification preference refresh failed:', error);
        return of(this.preferences$.value);
      })
    );
  }

  public savePreferences(preferences: NotificationPreferenceDto[]): Observable<void> {
    const nextState = {
      ...this.preferences$.value,
      preferences: preferences ?? []
    };
    this.applyPreferenceState(nextState);
    this.schedulePreferenceSave(this.preferences$.value.preferences);
    return of(void 0);
  }

  public setPreference(preference: NotificationPreferenceDto): Observable<void> {
    const current = [...this.preferences$.value.preferences];
    const index = current.findIndex(item => item.topic === preference.topic && item.channel === preference.channel);
    if (index >= 0) {
      current[index] = { ...current[index], ...preference };
    } else {
      current.push({
        severityThreshold: 'Info',
        ...preference
      });
    }

    return this.savePreferences(current);
  }

  public refreshDevices(): Observable<NotificationDeviceDto[]> {
    return this.http.get<NotificationDeviceDto[]>(`${this.apiUrl}/devices`).pipe(
      tap(devices => this.devices$.next(devices ?? [])),
      catchError((error) => {
        console.error('Notification device refresh failed:', error);
        return of(this.devices$.value);
      })
    );
  }

  public disableDevice(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/devices/${id}`).pipe(
      tap(() => this.refreshDevices().subscribe())
    );
  }

  public refreshTelegramBinding(): Observable<UserTelegramBindingDto> {
    return this.http.get<UserTelegramBindingDto>(`${this.apiUrl}/telegram`).pipe(
      map(binding => ({
        ...this.defaultTelegramBinding(),
        ...(binding ?? {}),
        personalTopicRoutes: binding?.personalTopicRoutes ?? []
      })),
      tap(binding => this.telegramBinding$.next(binding)),
      catchError((error) => {
        console.error('Telegram notification binding refresh failed:', error);
        return of(this.telegramBinding$.value);
      })
    );
  }

  public createTelegramActivation(): Observable<TelegramActivationResponse> {
    return this.http.post<TelegramActivationResponse>(`${this.apiUrl}/telegram/activation`, {}).pipe(
      tap(() => this.refreshTelegramBinding().subscribe())
    );
  }

  public disconnectTelegram(): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/telegram`).pipe(
      tap(() => this.refreshTelegramBinding().subscribe())
    );
  }

  public syncTelegramPersonalTopics(): Observable<TelegramTopicSyncResponse> {
    return this.http.post<TelegramTopicSyncResponse>(`${this.apiUrl}/telegram/personal/sync-topics`, {}).pipe(
      tap(response => {
        const current = this.telegramBinding$.value;
        this.telegramBinding$.next({
          ...current,
          personalTopicRoutes: response?.routes ?? current.personalTopicRoutes,
          personalTopicError: response?.error ?? null
        });
        this.refreshTelegramBinding().subscribe();
      })
    );
  }

  public refreshTelegramGroup(): Observable<TelegramGroupStatusDto> {
    return this.http.get<TelegramGroupStatusDto>(`${this.apiUrl}/telegram/group`).pipe(
      map(status => ({
        ...this.defaultTelegramGroupStatus(),
        ...(status ?? {}),
        routes: status?.routes ?? []
      })),
      tap(status => this.telegramGroupStatus$.next(status)),
      catchError((error) => {
        console.error('Telegram group status refresh failed:', error);
        return of(this.telegramGroupStatus$.value);
      })
    );
  }

  public syncTelegramGroupTopics(): Observable<TelegramTopicSyncResponse> {
    return this.http.post<TelegramTopicSyncResponse>(`${this.apiUrl}/telegram/group/sync-topics`, {}).pipe(
      tap(response => {
        const current = this.telegramGroupStatus$.value;
        this.telegramGroupStatus$.next({
          ...current,
          routes: response?.routes ?? current.routes,
          error: response?.error ?? current.error
        });
        this.refreshTelegramGroup().subscribe();
      })
    );
  }

  public createTelegramGroupInvite(): Observable<TelegramGroupInviteResponse> {
    return this.http.post<TelegramGroupInviteResponse>(`${this.apiUrl}/telegram/group/invite`, {});
  }

  public refreshMuteState(): Observable<NotificationMuteStateDto> {
    return this.http.get<NotificationMuteStateDto>(`${this.apiUrl}/mute`).pipe(
      tap(state => this.applyMuteState(state ?? { isMuted: false, mutedUntilUtc: null })),
      catchError((error) => {
        console.error('Notification mute state refresh failed:', error);
        this.applyMuteState({ isMuted: false, mutedUntilUtc: null });
        return of(this.muteState$.value);
      })
    );
  }

  public muteForSeconds(seconds: number): Observable<NotificationMuteStateDto> {
    const mutedUntilUtc = new Date(Date.now() + Math.max(60, seconds) * 1000).toISOString();
    return this.http.put<NotificationMuteStateDto>(`${this.apiUrl}/mute`, { mutedUntilUtc }).pipe(
      tap(state => this.applyMuteState(state))
    );
  }

  public unmute(): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/mute`).pipe(
      tap(() => this.applyMuteState({ isMuted: false, mutedUntilUtc: null }))
    );
  }

  private setChannelPreference(topic: string, channel: NotificationChannel, enabled: boolean): Observable<void> {
    return this.refreshPreferences().pipe(
      switchMap(() => this.setPreference({ topic, channel, enabled, severityThreshold: 'Info' }))
    );
  }

  private disableChannelForAllTopics(channel: NotificationChannel): Observable<void> {
    return this.refreshPreferences().pipe(
      switchMap((response) => {
        const preferences = response.preferences.map(preference =>
          preference.channel === channel ? { ...preference, enabled: false } : preference
        );
        return this.savePreferences(preferences);
      })
    );
  }

  private enableChannelForAllTopics(channel: NotificationChannel): Observable<void> {
    return this.refreshPreferences().pipe(
      switchMap((response) => {
        const preferences = response.preferences.map(preference =>
          preference.channel === channel ? { ...preference, enabled: true } : preference
        );
        return this.savePreferences(preferences);
      })
    );
  }

  private updateToken(newToken: string | null, oldToken: string | null = null): void {
    if (newToken) {
      this.registerDevice(newToken, oldToken).subscribe((device) => {
        if (device) {
          this.persistToken(newToken);
        }
      });
    } else {
      this.persistToken(null);
    }
  }

  private registerDevice(newToken: string, oldToken: string | null): Observable<NotificationDeviceDto> {
    const body = {
      token: newToken,
      oldToken,
      platform: 'Web',
      browser: this.resolveBrowserName(),
      deviceName: `${this.resolveBrowserName()} browser`
    };

    return this.http.post<NotificationDeviceDto>(`${this.apiUrl}/devices`, body).pipe(
      tap(() => this.refreshDevices().subscribe()),
      catchError((error) => {
        console.error('Token registration failed:', error);
        return of(null as NotificationDeviceDto);
      })
    );
  }

  private persistToken(newToken: string | null): void {
    if (newToken) {
      localStorage.setItem(this.TOKEN_KEY, newToken);
    } else {
      localStorage.removeItem(this.TOKEN_KEY);
    }

    this.token$.next(newToken);
  }

  private defaultTelegramGroupStatus(): TelegramGroupStatusDto {
    return {
      isAdmin: false,
      configured: false,
      botConfigured: false,
      chatConfigured: false,
      canSyncTopics: false,
      canInviteUsers: false,
      personalTelegramConnected: false,
      canCreateInvite: false,
      chatType: null,
      forumEnabled: false,
      groupActivationCommand: null,
      routes: []
    };
  }

  private defaultTelegramBinding(): UserTelegramBindingDto {
    return {
      enabled: false,
      canSyncPersonalTopics: false,
      personalTopicError: null,
      personalTopicRoutes: []
    };
  }

  private getTokenFromLocalStorage(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  private clearNotificationMessages(query: NotificationQuery): void {
    const clearQuery = this.normalizeNotificationQuery(query);
    const params = this.buildNotificationParams(clearQuery, false);

    this.http.post<void>(`${this.apiUrl}/clear`, {}, { params }).pipe(
      tap(() => {
        this.currentPage = 1;
        this.refreshNotifications(clearQuery).subscribe();
      })
    ).subscribe();
  }

  private buildNotificationParams(query: NotificationQuery, includePaging: boolean): HttpParams {
    let params = new HttpParams();

    if (includePaging) {
      const skip = (this.currentPage - 1) * this.PAGE_SIZE;
      params = params
        .set('skip', skip)
        .set('take', this.PAGE_SIZE);
    }

    Object.entries(this.normalizeNotificationQuery(query)).forEach(([key, value]) => {
      if (value) {
        params = params.set(key, value);
      }
    });

    return params;
  }

  private normalizeNotificationQuery(query: NotificationQuery = {}): NotificationQuery {
    return {
      search: query.search?.trim() || undefined,
      topic: query.topic || undefined,
      severity: query.severity || undefined,
      readState: query.readState || undefined
    };
  }

  private applyPreferenceState(state: NotificationPreferencesResponse, markConfirmed = false): void {
    const safeState = this.clonePreferenceState(state);
    this.preferences$.next(safeState);
    this.updateSubscribedTopicsFromPreferences(safeState.preferences);

    if (markConfirmed) {
      this.confirmedPreferenceState = this.clonePreferenceState(safeState);
    }
  }

  private clonePreferenceState(state: NotificationPreferencesResponse): NotificationPreferencesResponse {
    return {
      topics: [...(state?.topics ?? [])],
      channels: [...(state?.channels ?? [])],
      preferences: (state?.preferences ?? []).map(preference => ({ ...preference }))
    };
  }

  private clonePreferences(preferences: NotificationPreferenceDto[]): NotificationPreferenceDto[] {
    return (preferences ?? []).map(preference => ({ ...preference }));
  }

  private schedulePreferenceSave(preferences: NotificationPreferenceDto[]): void {
    const version = ++this.preferenceStateVersion;
    this.pendingPreferenceSave = {
      preferences: this.clonePreferences(preferences),
      version
    };

    if (this.preferenceSaveTimer) {
      clearTimeout(this.preferenceSaveTimer);
    }

    this.preferenceSaveTimer = setTimeout(() => this.flushPreferenceSave(), 180);
  }

  private flushPreferenceSave(): void {
    if (this.preferenceSaveInFlight || !this.pendingPreferenceSave) {
      return;
    }

    if (this.preferenceSaveTimer) {
      clearTimeout(this.preferenceSaveTimer);
      this.preferenceSaveTimer = null;
    }

    const save = this.pendingPreferenceSave;
    this.pendingPreferenceSave = null;
    this.preferenceSaveInFlight = true;

    this.http.put<void>(`${this.apiUrl}/preferences`, { preferences: save.preferences }).subscribe({
      next: () => {
        if (this.preferenceStateVersion === save.version && !this.pendingPreferenceSave) {
          this.confirmedPreferenceState = this.clonePreferenceState(this.preferences$.value);
        }
      },
      error: (error) => {
        console.error('Notification preference save failed:', error);
        if (this.preferenceStateVersion === save.version && !this.pendingPreferenceSave) {
          this.applyPreferenceState(this.confirmedPreferenceState);
        }
        this.preferenceSaveInFlight = false;
        if (this.pendingPreferenceSave) {
          this.flushPreferenceSave();
        }
      },
      complete: () => {
        this.preferenceSaveInFlight = false;
        if (this.pendingPreferenceSave) {
          this.flushPreferenceSave();
        }
      }
    });
  }

  private updateSubscribedTopicsFromPreferences(preferences: NotificationPreferenceDto[]): void {
    const topics = new Set(
      preferences
        .filter(preference => preference.channel === 'BrowserPush' && preference.enabled)
        .map(preference => preference.topic)
    );
    this.subscribedTopics$.next(topics);
  }

  private updateNotificationsData(totalMessages: number, unreadMessages: number): void {
    const totalPages = Math.max(1, Math.ceil(totalMessages / this.PAGE_SIZE));
    const currentPage = Math.min(this.currentPage, totalPages);
    const notificationsData = new NotificationsData(totalPages, this.PAGE_SIZE, currentPage, totalMessages, unreadMessages);
    this.notificationsData$.next(notificationsData);
  }

  private applyRealtimeUnread(unread: number | null | undefined): void {
    if (!Number.isFinite(unread)) {
      return;
    }

    const current = this.notificationsData$.value;
    this.notificationsData$.next(new NotificationsData(
      current.totalPages,
      current.pageSize,
      current.currentPage,
      current.totalMessages,
      Math.max(0, Number(unread))
    ));
  }

  private listenToMessages(): void {
    if (this.foregroundMessageSubscription) {
      return;
    }

    try {
      this.foregroundMessageSubscription = this.afMessaging.messages.subscribe({
        next: (message: any) => {
          this.notificationRefreshRequests$.next();
          const muted = this.isWebNotificationMuted(message);
          if (muted) {
            return;
          }

          this.showForegroundBrowserNotification(message);
          if (message?.data?.NotificationType === 'Alarm') {
            this.mediaPlayback.playNotificationSound('error', 5);
          }
        },
        error: (err) => console.error('FCM message listener error:', err)
      });
    } catch (err) {
      console.error('FCM message listener initialization error:', err);
    }
  }

  private async resolveFirebaseMessagingToken(): Promise<string | null> {
    const registration = await this.ensureFirebaseMessagingServiceWorker();
    const messaging = this.resolveFirebaseMessaging();

    if (messaging?.getToken) {
      return await messaging.getToken({ serviceWorkerRegistration: registration });
    }

    return await new Promise<string | null>((resolve, reject) => {
      const subscription = this.afMessaging.getToken.subscribe({
        next: (token) => {
          resolve(token ?? null);
          subscription.unsubscribe();
        },
        error: reject,
        complete: () => resolve(null),
      });
    });
  }

  private async ensureFirebaseMessagingServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return undefined;
    }

    return await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  }

  private resolveFirebaseMessaging(): any {
    const firebaseNamespace = (firebase as any)?.default ?? (firebase as any);
    return typeof firebaseNamespace?.messaging === 'function'
      ? firebaseNamespace.messaging()
      : null;
  }

  private showForegroundBrowserNotification(message: any): void {
    if (this.isWebNotificationMuted(message) || typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return;
    }

    const title = message?.notification?.title || message?.data?.Title || 'Notification';
    const body = message?.notification?.body || message?.data?.Body || '';
    const link = message?.fcmOptions?.link || message?.data?.Link || '/notifications';
    const notification = new Notification(title, {
      body,
      icon: './assets/images/logo/PWALogo.png',
      badge: './assets/images/logo/navizard.png',
      data: { link }
    });

    notification.onclick = () => {
      window.focus();
      if (link) {
        window.open(link, '_blank', 'noopener,noreferrer');
      }
      notification.close();
    };
  }

  private isMessagingAvailable(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false;
    }

    const hasFirebaseMessagingFactory = typeof (firebase as any)?.messaging === 'function';
    return hasFirebaseMessagingFactory && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  }

  private mapNotificationItem(item: any): NotificationListItem {
    const payload = this.parsePayload(item.payloadJson);
    const title = item.title || item.titleKey || 'Notifications.Events.Generic.Title';
    const body = item.body || item.bodyKey || 'Notifications.Events.Generic.Body';

    return {
      ...item,
      payload,
      timestamp: item.createdAtUtc,
      read: !!item.readAtUtc,
      notification: { title, body },
      fcmOptions: { link: item.link }
    };
  }

  private parsePayload(payloadJson?: string): Record<string, unknown> {
    if (!payloadJson) {
      return {};
    }

    try {
      return JSON.parse(payloadJson);
    } catch {
      return {};
    }
  }

  private applyMuteState(state: NotificationMuteStateDto): void {
    const normalizedState: NotificationMuteStateDto = {
      isMuted: !!state?.isMuted,
      mutedUntilUtc: state?.mutedUntilUtc ?? null
    };
    const remainingSeconds = this.calculateMuteRemainingSeconds(normalizedState);
    const effectiveState: NotificationMuteStateDto = normalizedState.isMuted && remainingSeconds > 0
      ? normalizedState
      : { isMuted: false, mutedUntilUtc: null };

    this.muteState$.next(effectiveState);
    this.muteRemainingSeconds$.next(remainingSeconds);
    this.toastNotify.syncWebMuteState(effectiveState.isMuted, remainingSeconds);
  }

  private startMuteCountdown(): void {
    if (this.muteCountdownSubscription) {
      return;
    }

    this.muteCountdownSubscription = interval(1000).subscribe(() => {
      const state = this.muteState$.value;
      const remaining = this.calculateMuteRemainingSeconds(state);
      this.muteRemainingSeconds$.next(remaining);
      if (state.isMuted && remaining <= 0) {
        this.applyMuteState({ isMuted: false, mutedUntilUtc: null });
      }
    });
  }

  private resetSessionState(): void {
    this.currentPage = 1;
    this.currentQuery = {};
    this.messages$.next([]);
    this.notificationsData$.next(new NotificationsData(0, this.PAGE_SIZE, 1, 0, 0));

    const preferences = { topics: [], channels: [], preferences: [] } as NotificationPreferencesResponse;
    this.preferences$.next(preferences);
    this.confirmedPreferenceState = preferences;
    this.devices$.next([]);
    this.telegramBinding$.next(this.defaultTelegramBinding());
    this.telegramGroupStatus$.next(this.defaultTelegramGroupStatus());
    this.toastNotify.clearNotifications();
    this.applyMuteState({ isMuted: false, mutedUntilUtc: null });

    if (this.preferenceSaveTimer) {
      clearTimeout(this.preferenceSaveTimer);
      this.preferenceSaveTimer = null;
    }
    this.pendingPreferenceSave = null;
    this.preferenceSaveInFlight = false;
  }

  private calculateMuteRemainingSeconds(state: NotificationMuteStateDto): number {
    if (!state?.isMuted || !state.mutedUntilUtc) {
      return 0;
    }

    return Math.max(0, Math.ceil((new Date(state.mutedUntilUtc).getTime() - Date.now()) / 1000));
  }

  private isWebNotificationMuted(message?: { muted?: boolean; data?: Record<string, unknown> } | null): boolean {
    if (this.muteState$.value?.isMuted) {
      return true;
    }

    const muted = message?.muted ?? message?.data?.Muted ?? message?.data?.muted;
    return muted === true || muted === 'true' || muted === 'True';
  }

  private playNotificationSoundIfNeeded(): void {
    const latest = this.messages$.value[0];
    if (!latest || this.muteState$.value.isMuted) {
      return;
    }

    if (['Warning', 'Error', 'Critical'].includes(latest.severity)) {
      this.mediaPlayback.playNotificationSound(latest.severity === 'Warning' ? 'warning' : 'error', 5);
    }
  }

  private resolveBrowserName(): string {
    const userAgent = navigator?.userAgent ?? '';
    if (userAgent.includes('Firefox')) {
      return 'Firefox';
    }
    if (userAgent.includes('Edg/')) {
      return 'Edge';
    }
    if (userAgent.includes('Chrome')) {
      return 'Chrome';
    }
    if (userAgent.includes('Safari')) {
      return 'Safari';
    }
    return 'Browser';
  }
}
